import { __awaiter } from '../../../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { logger as logger$1 } from '@dynamic-labs/multi-wallet';
import { ICONIC_SPRITE_URL } from '@dynamic-labs/iconic';
import { AUTH_TOKEN, AUTH_USER, AUTH_MODE } from '../../utils/constants/localStorage.js';
import '../../utils/constants/colors.js';
import { DEFAULT_NUMBER_OF_WALLETS_TO_SHOW } from '../../utils/constants/values.js';
import { decodeJwt } from '../../shared/utils/functions/decodeJwt/decodeJwt.js';
import { logger } from '../../shared/logger.js';
import { ViewContextProvider } from '../ViewContext/ViewContext.js';
import { useLocalStorage } from '../../shared/utils/hooks/useLocalStorage/useLocalStorage.js';
import '../../shared/consts/index.js';
import { useFetchProjectSettings } from '../../utils/hooks/useFetchProjectSettings/useFetchProjectSettings.js';
import { useFetchNonce } from '../../utils/hooks/useFetchNonce/useFetchNonce.js';
import { useWallets } from '../../utils/hooks/useWallets/useWallets.js';
import { useWalletConnectors } from '../../utils/hooks/useWalletConnectors/useWalletConnectors.js';
import { revokeSession } from '../../data/api.js';
import { getAuthToken } from '../../utils/functions/getAuthToken/getAuthToken.js';
import '@dynamic-labs/sdk-api';
import { isSameWalletName } from '../../utils/functions/isSameWalletName/isSameWalletName.js';
import { ErrorContextProvider } from '../ErrorContext/ErrorContext.js';
import 'react-dom';
import { useWalletUiUtils } from '../../utils/hooks/useWalletUiUtils/useWalletUiUtils.js';
import '../MockContext/MockContext.js';
import { CaptchaContextProvider } from '../CaptchaContext/CaptchaContext.js';
import '../../utils/hooks/useUserUpdateRequest/useUserUpdateRequest.js';
import ApiEndpoint from '../../config/ApiEndpoint.js';
import { ThemeContextProvider } from '../ThemeContext/ThemeContext.js';
import { useFetchNetworkConfigurations } from '../../utils/hooks/useFetchNetworkConfigurations/useFetchNetworkConfigurations.js';
import { useMultiWalletWidgetState } from '../../utils/hooks/multiWallet/useMultiWalletWidgetState/useMultiWalletWidgetState.js';
import { useWalletEventListeners } from '../../utils/hooks/useWalletEventListeners/useWalletEventListeners.js';
import { LoadingContextProvider } from '../LoadingContext/LoadingContext.js';
import { useSyncPrimaryWallet } from '../../utils/hooks/multiWallet/useSyncPrimaryWallet/useSyncPrimaryWallet.js';
import { useFetchWalletsForChainsMap } from '../../utils/hooks/useFetchWalletsForChainsMap/useFetchWalletsForChainsMap.js';
import { useHandleUnlinkWallet } from '../../utils/hooks/useHandleUnlinkWallet/useHandleUnlinkWallet.js';
import { DynamicAuthFlow } from '../../app.js';
import { useClearWalletConnectSessions } from '../../utils/hooks/useClearWalletConnectSessions/useClearWalletConnectSessions.js';
import { parseTheme } from '../../utils/functions/parseTheme/parseTheme.js';
import { useConnectWallet } from '../../utils/hooks/useConnectWallet/useConnectWallet.js';
import { useRpcProviders } from '../../utils/hooks/useRpcProviders/useRpcProviders.js';
import { EmailVerificationContextProvider } from '../EmailVerificationContext/EmailVerificationContext.js';
import { DynamicWidgetContextProvider } from '../../widgets/DynamicWidget/context/DynamicWidgetContext.js';
import { QrCodeContextProvider } from '../QrCodeContext/QrCodeContext.js';
import { Toolkit } from '../../components/Toolkit/Toolkit.js';
import 'react-focus-lock';
import '../../components/Transition/ZoomTransition/ZoomTransition.js';
import '../../components/Transition/SlideInUpTransition/SlideInUpTransition.js';
import '../../components/Transition/OpacityTransition/OpacityTransition.js';
import '../../components/ShadowDOM/ShadowDOM.js';
import '../../components/IconButton/IconButton.js';
import { useGlobalLoading } from '../../shared/utils/hooks/useGlobalLoading/useGlobalLoading.js';
import { initExpirationTime } from './helpers.js';
import { validateAuthUser } from './validators.js';
import { useCustomerCallbacks } from './useCustomerCallbacks/useCustomerCallbacks.js';
import { useCustomerAuthFlowCallbacks } from './useCustomerCallbacks/useCustomerAuthFlowCallbacks.js';
import { useEmailLoginState } from './hooks/useEmailLoginState/useEmailLoginState.js';
import { useDisplayOrderState } from './hooks/useDisplayOrderState/useDisplayOrderState.js';

const EMPTY_INITIAL_USER = undefined;
const DynamicContext = createContext(undefined);
const DynamicContextProvider = ({ settings: { accessDeniedMessagePrimary, accessDeniedMessageSecondary, apiBaseUrl, appLogoUrl = 'https://demo.dynamic.xyz/assets/dynamic-logo.svg', appName = 'Dynamic Example', cssOverrides, defaultNumberOfWalletsToShow = DEFAULT_NUMBER_OF_WALLETS_TO_SHOW, initialAuthenticationMode = 'connect-and-sign', vendorCredentials, debugError = false, displayTermsOfService = true, newToWeb3WalletChainMap, enableVisitTrackingOnConnectOnly = true, environmentId, eventsCallbacks, evmNetworks, walletsFilter, logLevel = 'WARN', multiWallet = false, notInTheListImageUrl, onboardingImageUrl, policiesConsentInnerComponent, privacyPolicyUrl, signWithEmailWalletName, socialMediaLinkText, socialMediaIconUrl, socialMediaUrl, termsOfServiceUrl, toolkitEnabled, siweStatement, shadowDOMEnabled = true, magicLinkConfig, }, children, theme, }) => {
    var _a, _b;
    logger.setLogLevel(logLevel);
    logger$1.setLogLevel(logLevel);
    ApiEndpoint.setBaseUrl((_a = process.env.DYNAMIC_API_BASE_URL) !== null && _a !== void 0 ? _a : apiBaseUrl);
    const isAuthenticated = Boolean(getAuthToken());
    const [onboardingOnlyJwt, setOnboardingOnlyJwt] = useState(undefined);
    const [showAuthFlow, setShowAuthFlow] = useState(false);
    // Allows clients to control widget flow without need to click on AccountControl
    const [showDynamicUserProfile, setShowDynamicUserProfile] = useState(false);
    const [isSingleWalletAccount, setIsSingleWalletAccount] = useState(false);
    const [user, setUser, removeUser] = useLocalStorage(AUTH_USER, EMPTY_INITIAL_USER, validateAuthUser, [AUTH_TOKEN]);
    const { consumeNonce } = useFetchNonce(environmentId);
    const [qrcodeUri, setQrcodeUri] = useState('');
    const [desktopUri, setDesktopUri] = useState('');
    const [multiWalletWidgetState, setMultiWalletWidgetState, { awaiting_account_switch: [accountSwitchState], awaiting_signature: [awaitingSignatureState], },] = useMultiWalletWidgetState();
    const [isVerifying, setIsVerifying] = useState(false);
    const { projectSettings, isLoading: isProjectSettingsLoading } = useFetchProjectSettings({ authToken: getAuthToken(), environmentId });
    const { configurations: serverNetworkConfigurations, removeLsNetworks } = useFetchNetworkConfigurations({
        authToken: getAuthToken(),
        environmentId,
        localNetworks: evmNetworks,
    });
    const { imageUserInAccessList, imageUserNotInAccessList, displayName, appLogo, } = (projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.general) || {};
    appLogoUrl = appLogo || appLogoUrl;
    appName = displayName || appName;
    const [loginWithEmail, setLogInWithEmail] = useEmailLoginState((projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.providers) || [], user);
    const [authorizationViewDisplayOrder, setAuthorizationViewDisplayOrder] = useDisplayOrderState(projectSettings);
    const [confirmationModal, walletUiUtils] = useWalletUiUtils({
        appLogoUrl,
        appName,
        getAppOrigin: () => window.location.origin,
    });
    const { wallets } = useWallets({
        appLogoUrl,
        appName,
        magicLinkConfig,
        networkConfigurations: serverNetworkConfigurations,
        projectSettings,
        vendorCredentials,
        walletUiUtils,
    });
    const [authMode, setAuthMode] = useLocalStorage(AUTH_MODE, initialAuthenticationMode);
    const { connectWallet, removeConnectedInfo, connectedInfo, connectedWallet, setConnectedWallet, connectWalletByAddressAndChain, } = useConnectWallet(wallets, authMode, environmentId, enableVisitTrackingOnConnectOnly);
    const [selectedWalletConnector, setSelectedWalletConnector] = useState(null);
    const removeAuthTokenAndUser = useCallback(() => {
        localStorage.removeItem(AUTH_TOKEN);
        removeUser();
    }, [removeUser]);
    const { clearPrimaryWalletId, computeConnectedStateForWallets, didConnectedStateLoad, walletConnectors, primaryWallet, primaryWalletId, showQrcodeModal, secondaryWallets, setPrimaryWallet, setPrimaryWalletId, setShowQrcodeModal, } = useWalletConnectors({
        authToken: getAuthToken() || onboardingOnlyJwt,
        multiWalletWidgetState,
        setDesktopUri,
        setMultiWalletWidgetState,
        setQrcodeUri,
        wallets,
    });
    const { sdkHasLoaded, setSdkHasLoaded } = useGlobalLoading({
        authMode,
        connectedInfo,
        connectedWallet,
        primaryWallet,
        projectSettings,
        user,
    });
    const walletForUserFacingContext = authMode === 'connect-and-sign' || user ? primaryWallet : connectedWallet;
    useSyncPrimaryWallet({
        canSync: didConnectedStateLoad,
        multiWalletWidgetState,
        primaryWallet,
        setMultiWalletWidgetState,
    });
    const { clearAllWalletConnectSessions, clearOphanedWalletConnectSessions } = useClearWalletConnectSessions({
        connectors: walletConnectors,
    });
    useEffect(clearOphanedWalletConnectSessions, [
        clearOphanedWalletConnectSessions,
    ]);
    const handleLogOut = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e, _f;
        clearAllWalletConnectSessions();
        const jwt = getAuthToken();
        if (jwt && user) {
            // do not await this async call - just fire and forget
            revokeSession({ environmentId, sessionId: user.sessionId }, jwt);
        }
        for (const connector of walletConnectors) {
            yield ((_c = connector.endSession) === null || _c === void 0 ? void 0 : _c.call(connector));
        }
        (_e = (_d = connectedWallet === null || connectedWallet === void 0 ? void 0 : connectedWallet.connector) === null || _d === void 0 ? void 0 : _d.endSession) === null || _e === void 0 ? void 0 : _e.call(_d);
        setShowAuthFlow(false);
        setMultiWalletWidgetState('idle');
        removeAuthTokenAndUser();
        setConnectedWallet(null);
        removeLsNetworks();
        setOnboardingOnlyJwt(undefined);
        removeConnectedInfo();
        clearPrimaryWalletId();
        (_f = eventsCallbacks === null || eventsCallbacks === void 0 ? void 0 : eventsCallbacks.onLogout) === null || _f === void 0 ? void 0 : _f.call(eventsCallbacks, user);
    }), [
        clearAllWalletConnectSessions,
        user,
        connectedWallet === null || connectedWallet === void 0 ? void 0 : connectedWallet.connector,
        setMultiWalletWidgetState,
        removeAuthTokenAndUser,
        setConnectedWallet,
        removeLsNetworks,
        removeConnectedInfo,
        clearPrimaryWalletId,
        eventsCallbacks,
        environmentId,
        walletConnectors,
    ]);
    const handleUnlinkWallet = useHandleUnlinkWallet({
        environmentId,
        primaryWalletId,
        secondaryWallets,
        setUser,
        verifiedCredentials: (user === null || user === void 0 ? void 0 : user.verifiedCredentials) || [],
    });
    const { loadingNetwork, network, setNetwork } = useWalletEventListeners({
        computeConnectedStateForWallets,
        connectWalletByAddressAndChain,
        handleLogOut,
        multiWallet,
        multiWalletWidgetState,
        primaryWallet: walletForUserFacingContext,
        secondaryWallets,
        selectedWalletConnector,
        setMultiWalletWidgetState,
        setPrimaryWalletId,
        setSelectedWalletConnector,
        user,
    });
    const { walletsForChainsMap } = useFetchWalletsForChainsMap(newToWeb3WalletChainMap);
    const rpcProviders = useRpcProviders({
        networkConfigurations: serverNetworkConfigurations,
        networkId: network,
    });
    useEffect(() => {
        initExpirationTime(handleLogOut);
    }, [handleLogOut]);
    useCustomerAuthFlowCallbacks({ eventsCallbacks, showAuthFlow });
    const { setCallback } = useCustomerCallbacks({
        authMode,
        callbacks: {
            onAuthSuccess: eventsCallbacks === null || eventsCallbacks === void 0 ? void 0 : eventsCallbacks.onAuthSuccess,
            onConnectSuccess: eventsCallbacks === null || eventsCallbacks === void 0 ? void 0 : eventsCallbacks.onConnectSuccess,
            onLinkSuccess: eventsCallbacks === null || eventsCallbacks === void 0 ? void 0 : eventsCallbacks.onLinkSuccess,
            onUserProfileUpdate: eventsCallbacks === null || eventsCallbacks === void 0 ? void 0 : eventsCallbacks.onUserProfileUpdate,
        },
        handleLogOut,
        isAuthenticated,
        primaryWallet: walletForUserFacingContext,
        user,
    });
    const clearStatesOnBackClick = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _g;
        setDesktopUri('');
        setIsVerifying(false);
        if (!user) {
            clearAllWalletConnectSessions();
        }
        setMultiWalletWidgetState('idle');
        if (authMode !== 'connect-only') {
            setConnectedWallet(null);
            removeConnectedInfo();
        }
        if (!localStorage.getItem(AUTH_USER)) {
            localStorage.removeItem(AUTH_TOKEN);
        }
        if (selectedWalletConnector &&
            (!primaryWallet ||
                !isSameWalletName(primaryWallet.connector.name, selectedWalletConnector.name))) {
            yield ((_g = selectedWalletConnector === null || selectedWalletConnector === void 0 ? void 0 : selectedWalletConnector.endSession) === null || _g === void 0 ? void 0 : _g.call(selectedWalletConnector));
        }
    }), [
        user,
        setMultiWalletWidgetState,
        authMode,
        selectedWalletConnector,
        primaryWallet,
        clearAllWalletConnectSessions,
        setConnectedWallet,
        removeConnectedInfo,
    ]);
    const value = useMemo(() => {
        var _a, _b;
        return ({
            accessDeniedMessagePrimary,
            accessDeniedMessageSecondary,
            accountSwitchState,
            appLogoUrl,
            appName,
            authMode,
            authToken: getAuthToken(),
            authorizationViewDisplayOrder,
            awaitingSignatureState,
            clearStatesOnBackClick,
            computeConnectedStateForWallets,
            connectWallet,
            connectedWallet,
            connectedWallets: primaryWallet
                ? [primaryWallet, ...secondaryWallets]
                : [],
            consumeNonce,
            cssOverrides,
            debugError,
            decodedAuthToken: decodeJwt(getAuthToken()),
            decodedOnboardingOnlyToken: decodeJwt(onboardingOnlyJwt),
            defaultNumberOfWalletsToShow,
            desktopUri,
            displayTermsOfService,
            environmentId,
            eventsCallbacks,
            handleLogOut,
            handleUnlinkWallet,
            isAuthenticated,
            isProjectSettingsLoading,
            isSingleWalletAccount,
            isVerifying,
            loadingNetwork,
            loginWithEmail,
            multiWallet,
            multiWalletWidgetState,
            network,
            networkConfigurations: serverNetworkConfigurations,
            newToWeb3WalletChainMap: walletsForChainsMap,
            notInTheListImageUrl: imageUserNotInAccessList || notInTheListImageUrl,
            onboardingImageUrl: imageUserInAccessList || onboardingImageUrl,
            onboardingOnlyJwt,
            policiesConsentInnerComponent,
            primaryWallet: walletForUserFacingContext,
            primaryWalletId,
            privacyPolicyUrl,
            projectSettings,
            qrcodeUri,
            rpcProviders,
            sdkHasLoaded,
            secondaryWallets,
            selectedWalletConnector,
            setAuthMode,
            setAuthorizationViewDisplayOrder,
            setCallback,
            setDesktopUri,
            setIsSingleWalletAccount,
            setIsVerifying,
            setLogInWithEmail,
            setMultiWalletWidgetState,
            setNetwork,
            setOnboardingOnlyJwt,
            setPrimaryWallet,
            setPrimaryWalletId,
            setQrcodeUri,
            setSdkHasLoaded,
            setSelectedWalletConnector,
            setShowAuthFlow,
            setShowDynamicUserProfile,
            setShowQrcodeModal,
            setUser,
            shadowDOMEnabled,
            showAuthFlow,
            showDynamicUserProfile,
            showQrcodeModal,
            signWithEmailWalletName,
            siweStatement,
            socialMediaIconUrl: ((_a = projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.general) === null || _a === void 0 ? void 0 : _a.supportUrls) || socialMediaIconUrl,
            socialMediaLinkText,
            socialMediaUrl,
            termsOfServiceUrl,
            theme,
            toolkitEnabled,
            user,
            vendorCredentials,
            walletConnector: (_b = walletForUserFacingContext === null || walletForUserFacingContext === void 0 ? void 0 : walletForUserFacingContext.connector) !== null && _b !== void 0 ? _b : null,
            walletUiUtils,
            wallets,
            walletsFilter,
        });
    }, [
        accessDeniedMessagePrimary,
        accessDeniedMessageSecondary,
        accountSwitchState,
        appLogoUrl,
        appName,
        authMode,
        authorizationViewDisplayOrder,
        awaitingSignatureState,
        clearStatesOnBackClick,
        computeConnectedStateForWallets,
        connectWallet,
        connectedWallet,
        primaryWallet,
        secondaryWallets,
        consumeNonce,
        cssOverrides,
        debugError,
        onboardingOnlyJwt,
        defaultNumberOfWalletsToShow,
        desktopUri,
        displayTermsOfService,
        environmentId,
        eventsCallbacks,
        handleLogOut,
        handleUnlinkWallet,
        isAuthenticated,
        isProjectSettingsLoading,
        isSingleWalletAccount,
        isVerifying,
        loadingNetwork,
        loginWithEmail,
        multiWallet,
        multiWalletWidgetState,
        network,
        serverNetworkConfigurations,
        walletsForChainsMap,
        imageUserNotInAccessList,
        notInTheListImageUrl,
        imageUserInAccessList,
        onboardingImageUrl,
        policiesConsentInnerComponent,
        walletForUserFacingContext,
        primaryWalletId,
        privacyPolicyUrl,
        projectSettings,
        qrcodeUri,
        rpcProviders,
        sdkHasLoaded,
        selectedWalletConnector,
        setAuthMode,
        setAuthorizationViewDisplayOrder,
        setCallback,
        setLogInWithEmail,
        setMultiWalletWidgetState,
        setNetwork,
        setPrimaryWallet,
        setPrimaryWalletId,
        setSdkHasLoaded,
        setShowQrcodeModal,
        setUser,
        shadowDOMEnabled,
        showAuthFlow,
        showDynamicUserProfile,
        showQrcodeModal,
        signWithEmailWalletName,
        siweStatement,
        socialMediaIconUrl,
        socialMediaLinkText,
        socialMediaUrl,
        termsOfServiceUrl,
        theme,
        toolkitEnabled,
        user,
        vendorCredentials,
        walletUiUtils,
        wallets,
        walletsFilter,
    ]);
    useEffect(() => {
        if (!document.head.querySelector('#sprite')) {
            const element = document.createElement('link');
            element.setAttribute('id', 'sprite');
            element.setAttribute('rel', 'prefetch');
            element.setAttribute('href', ICONIC_SPRITE_URL);
            element.setAttribute('as', 'image');
            element.setAttribute('type', 'image/svg+xml');
            document.head.insertAdjacentElement('beforeend', element);
        }
    }, []);
    // hides dynamic widget if user is not logged in or there is no connected wallet
    useEffect(() => {
        if (!user && !connectedWallet && showDynamicUserProfile) {
            setShowDynamicUserProfile(false);
        }
    }, [connectedWallet, showDynamicUserProfile, user]);
    // DYN-1140 - Opens Widget when multiWalletWidgetState changes
    useEffect(() => {
        // If statement to secure SingleWallet widget
        if (!multiWallet)
            return;
        // We need to make sure that the user is logged in so that we do not try to display the widget before authorization
        if (user && multiWalletWidgetState !== 'idle' && !showDynamicUserProfile) {
            setShowDynamicUserProfile(true);
        }
    }, [multiWallet, multiWalletWidgetState, showDynamicUserProfile, user]);
    return (jsx(DynamicContext.Provider, Object.assign({ value: value }, { children: jsx(ThemeContextProvider, Object.assign({ customerTheme: parseTheme(theme, ((_b = projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.design) === null || _b === void 0 ? void 0 : _b.modal) || undefined), designSettings: projectSettings === null || projectSettings === void 0 ? void 0 : projectSettings.design }, { children: jsx(LoadingContextProvider, { children: jsx(ViewContextProvider, Object.assign({ loginWithEmail: loginWithEmail }, { children: jsx(QrCodeContextProvider, { children: jsx(EmailVerificationContextProvider, { children: jsx(ErrorContextProvider, { children: jsx(DynamicWidgetContextProvider, { children: jsxs(CaptchaContextProvider, { children: [jsx(DynamicAuthFlow, {}), jsx(Toolkit, {}), confirmationModal, children] }) }) }) }) }) })) }) })) })));
};

export { DynamicContext, DynamicContextProvider };
